import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { D1Logo } from "@/components/D1Logo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    if (!identifier.trim() || !password) {
      toast.error("Informe CPF/Matrícula e senha");
      return;
    }
    setLoading(true);
    try {
      const u = await login(identifier.trim(), password);
      toast.success(`Bem-vindo, ${u.nome.split(" ")[0]}!`);
      const dest = u.must_change_password
        ? "/trocar-senha"
        : u.role === "motorista"
          ? "/motorista"
          : "/admin";
      navigate(location.state?.from?.pathname || dest, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  async function onForgotPassword() {
    if (!identifier.trim()) {
      toast.error("Informe seu CPF ou matrícula primeiro");
      return;
    }
    setResetting(true);
    try {
      const { data } = await api.post("/auth/forgot-password", {
        identifier: identifier.trim(),
      });
      toast.success(data?.message || "Pedido enviado ao administrador");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setResetting(false);
    }
  }

  return (
    <div className="min-h-screen w-full bg-slate-50 lg:grid lg:grid-cols-[1.05fr_1fr]">
      {/* Left: brand panel */}
      <div className="relative hidden overflow-hidden bg-gradient-to-br from-[#0B1120] via-[#0F1B33] to-[#1E3A8A] lg:flex lg:flex-col lg:justify-between lg:p-12">
        <div className="absolute inset-0 bg-grid-dark opacity-40" />
        <div className="relative z-10">
          <D1Logo variant="dark" />
        </div>
        <div className="relative z-10 max-w-md">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-blue-300">
            Torre de Controle
          </p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight text-white sm:text-5xl">
            Cada entrega.<br />
            Cada motorista.<br />
            <span className="text-blue-400">Sob controle.</span>
          </h1>
          <p className="mt-5 text-sm leading-relaxed text-slate-300">
            Gestão diária de fechamentos, conferência de comprovantes e indicadores de performance
            da operação logística D1.
          </p>
        </div>
        <div className="relative z-10 flex items-center gap-6 text-[11px] uppercase tracking-widest text-slate-400">
          <span>· Tempo Real</span>
          <span>· PWA</span>
          <span>· Multi-Rota</span>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex min-h-screen items-center justify-center p-6 lg:min-h-0">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <D1Logo variant="light" />
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
              Acesso restrito
            </p>
            <h2 className="mt-2 font-display text-3xl font-bold text-slate-900">Entrar</h2>
            <p className="mt-1 text-sm text-slate-500">
              Use seu CPF ou matrícula e a senha fornecida.
            </p>

            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="identifier" className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                  CPF ou Matrícula
                </Label>
                <Input
                  id="identifier"
                  data-testid="login-identifier-input"
                  autoComplete="username"
                  placeholder="000.000.000-00 ou MOT001"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="h-12"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                  Senha
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type={show ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label="Mostrar senha"
                  >
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="login-submit-button"
                disabled={loading}
                className="h-12 w-full bg-blue-900 text-base font-semibold hover:bg-blue-800"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <span className="inline-flex items-center gap-2">
                    Entrar <ArrowRight className="h-4 w-4" />
                  </span>
                )}
              </Button>

              <button
                type="button"
                onClick={onForgotPassword}
                disabled={resetting}
                className="block w-full text-center text-xs font-medium text-slate-500 underline-offset-4 hover:text-blue-700 hover:underline"
                data-testid="forgot-password-link"
              >
                {resetting ? "Enviando pedido…" : "Esqueci minha senha"}
              </button>
            </form>
          </div>

          <p className="mt-6 text-center text-[11px] uppercase tracking-widest text-slate-400">
            D1 Logística · Sistema Interno
            <span className="mt-2 block normal-case tracking-normal">
              Desenvolvido por Alpha Performance · Versão 1.5.2
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
